/**
 * SearchSelect - one searchable dropdown in place of a "Search…" box beside
 * a <select>. The closed control reads like an input showing the picked
 * label; click or focus opens the list, typing filters it, arrows + Enter
 * pick, Escape or a click outside closes. The first option is always the
 * default (value ''), e.g. "None (not under another project)".
 *
 * The list is portalled to <body> with fixed positioning, so a modal's
 * scroll area never clips it and a dragged modal does not leave it behind
 * (it follows the control on scroll / resize).
 *
 * Props:
 *   options       [{ value, label, text? }] - what typing searches (text, else label)
 *   browse        what shows with nothing typed (default: options)
 *   hint          a line above the list with nothing typed
 *   value, onChange(value)
 *   defaultLabel  the pinned first option's label (value '')
 *   valueLabel    the label when `value` is in no option (an off-list link)
 *   label         aria-label;  title, style, max (default 150 shown)
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { inputSt } from '../opportunities/editOpportunityConstants';

const listSt = {
  position: 'fixed',
  zIndex: 5000,
  background: '#fff',
  border: '1px solid #D8DED4',
  borderRadius: 6,
  boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
  maxHeight: 280,
  overflowY: 'auto',
  fontFamily: "'Montserrat', sans-serif",
};
const optionSt = {
  padding: '7px 12px',
  fontSize: 12,
  cursor: 'pointer',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #F3F3F0',
};

export function matchOptions(options, query, max = 150) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return options.slice(0, max);
  return options
    .filter((o) =>
      String(o.text != null ? o.text : o.label)
        .toLowerCase()
        .includes(q)
    )
    .slice(0, max);
}

export default function SearchSelect({
  options = [],
  browse,
  hint,
  value = '',
  onChange,
  defaultLabel = '-',
  valueLabel,
  label,
  title,
  style,
  max = 150,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();
  const listId = `${id}-list`;

  const v = String(value || '');
  const current = options.find((o) => String(o.value) === v) || (browse || []).find((o) => String(o.value) === v);
  const shownLabel = v ? (current ? current.label : valueLabel || v) : defaultLabel;
  const q = query.trim();
  const list = [{ value: '', label: defaultLabel }, ...(q ? matchOptions(options, q, max) : (browse || options).slice(0, max))];

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => inputRef.current && setRect(inputRef.current.getBoundingClientRect());
    place();
    const outside = (e) => {
      if (inputRef.current && inputRef.current.contains(e.target)) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', outside);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);
  useEffect(() => {
    const el = open ? document.getElementById(`${id}-o${active}`) : null;
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [open, active, id]);

  const show = () => {
    if (open) return;
    setQuery('');
    const i = list.findIndex((o) => String(o.value) === v);
    setActive(i < 0 ? 0 : i);
    setOpen(true);
  };
  const pick = (o) => {
    onChange(String(o.value));
    setOpen(false);
    setQuery('');
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) return show();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActive((a) => Math.max(0, Math.min(list.length - 1, a + step)));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      if (list[active]) pick(list[active]);
    } else if (e.key === 'Escape') {
      if (!open) return;
      e.preventDefault();
      e.stopPropagation(); // the modal's own Escape stays shut
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Tab') {
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        role="combobox"
        aria-label={label}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open ? `${id}-o${active}` : undefined}
        autoComplete="off"
        title={title}
        style={{ ...inputSt, minWidth: 0, cursor: 'pointer', color: v ? '#0D0D0D' : '#777', ...style }}
        value={open ? query : shownLabel}
        placeholder={shownLabel}
        onFocus={show}
        onClick={show}
        onChange={(e) => {
          if (!open) setOpen(true);
          setQuery(e.target.value);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      {open &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={label ? `${label} list` : undefined}
            style={{
              ...listSt,
              top: rect ? rect.bottom + 2 : 0,
              left: rect ? rect.left : 0,
              width: rect ? Math.max(rect.width, 220) : 260,
            }}
          >
            {!q && hint && <div style={{ padding: '6px 12px', fontSize: 10.5, color: '#777', borderBottom: '1px solid #EEE' }}>{hint}</div>}
            {list.map((o, i) => (
              <div
                key={`${o.value}-${i}`}
                id={`${id}-o${i}`}
                role="option"
                aria-selected={String(o.value) === v}
                onMouseDown={(e) => {
                  e.preventDefault(); // the input keeps the focus
                  pick(o);
                }}
                onMouseEnter={() => setActive(i)}
                style={{
                  ...optionSt,
                  color: o.value === '' ? '#777' : undefined,
                  fontWeight: String(o.value) === v ? 700 : 500,
                  background: i === active ? '#F0F7EE' : undefined,
                }}
              >
                {o.label}
              </div>
            ))}
            {q && list.length === 1 && <div style={{ ...optionSt, color: '#999', cursor: 'default' }}>No match</div>}
          </div>,
          document.body
        )}
    </>
  );
}
